import type { HistoryAction, PointGroup } from './matchHistory.types';

/**
 * Types qui ne font pas partie d'un rallye mais s'y rattachent : ils ouvrent ou
 * interrompent le jeu sans le clore. On les garde dans le point courant, comme
 * contexte — un temps mort au milieu d'un set raconte quelque chose.
 */
const CONTEXT_TYPES = new Set([
  'SET_START',
  'Temps Mort',
  'Temps Mort Adversaire',
  'Substitution',
  'LIBERO_CHANGE',
  'MATCH_END',
]);

export function isContextAction(action: HistoryAction): boolean {
  return CONTEXT_TYPES.has(action.type);
}

/** Camp qui a marqué, déduit du score qui a bougé entre deux points. */
function winnerOf(
  previous: { us: number; them: number },
  current: { us: number; them: number },
): 'us' | 'them' | null {
  if (current.us > previous.us) return 'us';
  if (current.them > previous.them) return 'them';
  return null;
}

/**
 * Timeline plate → points, du plus ancien au plus récent.
 *
 * Un point se ferme sur son action terminale. Un rallye en cours en fin de
 * liste reste ouvert plutôt que d'être jeté : c'est le cas normal d'un set en
 * direct, et ses actions doivent rester visibles au coach.
 */
export function groupByPoint(actions: HistoryAction[]): PointGroup[] {
  const ordered = [...actions].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  const groups: PointGroup[] = [];

  let current: PointGroup | null = null;
  let pointNumberBySet = new Map<number, number>();
  let previousScore = { us: 0, them: 0 };

  const openGroup = (action: HistoryAction): PointGroup => {
    const rank = (pointNumberBySet.get(action.setNumber) ?? 0) + 1;
    pointNumberBySet.set(action.setNumber, rank);
    return {
      pointNumber: rank,
      setNumber: action.setNumber,
      scoreUs: action.expectedScoreUs,
      scoreThem: action.expectedScoreThem,
      winner: null,
      open: true,
      actions: [],
    };
  };

  for (const action of ordered) {
    // Changement de set : le point courant appartenait au set précédent.
    if (current && action.setNumber !== current.setNumber) {
      current = null;
      previousScore = { us: 0, them: 0 };
    }
    if (!current) {
      current = openGroup(action);
      groups.push(current);
    }

    current.actions.push(action);

    if (isContextAction(action)) {
      continue;
    }

    if (action.isTerminal) {
      current.scoreUs = action.expectedScoreUs;
      current.scoreThem = action.expectedScoreThem;
      current.winner = winnerOf(previousScore, {
        us: action.expectedScoreUs,
        them: action.expectedScoreThem,
      });
      current.open = false;
      previousScore = { us: action.expectedScoreUs, them: action.expectedScoreThem };
      current = null;
    }
  }

  return groups;
}

/**
 * Points du plus récent au plus ancien, et dans chaque point les actions dans
 * le même sens : l'ordre de lecture du panneau. Mélanger les deux sens obligeait
 * à lire le point de bas en haut quand la liste se lit de haut en bas.
 */
export function groupByPointDescending(actions: HistoryAction[]): PointGroup[] {
  return groupByPoint(actions)
    .reverse()
    .map((point) => ({ ...point, actions: [...point.actions].reverse() }));
}
